/**
 * Nombres de los eventos de producto. Módulo puro: cero imports.
 *
 * Los nombres son datos compartidos entre servidor y cliente. Antes vivían en
 * `lib/product-analytics.ts`, que arranca `instrumentation-client` (PostHog de
 * navegador), así que un server component que solo necesitaba el nombre de un
 * evento estaba metiendo el grafo de analítica del navegador en el grafo de
 * servidor (revisión de #450). El build lo toleraba porque el tree-shaking
 * recortaba el uso de `captureEvent`, pero la frontera era accidental: un side
 * effect futuro en ese módulo la rompería.
 *
 * `lib/product-analytics.ts` re-exporta este objeto para los componentes de
 * cliente, que ya lo importaban. Los server components importan de aquí.
 */
export const PRODUCT_EVENTS = {
  VIDEO_FORM_STARTED: 'video_form_started',
  VIDEO_FORM_STEP_COMPLETED: 'video_form_step_completed',
  /** Se pintó un paso del wizard: `step` con los mismos valores que step_completed. */
  VIDEO_FORM_STEP_VIEWED: 'video_form_step_viewed',
  /** Una validación bloqueó el avance o el pago: `step`, `field` y `error_code`, nunca el valor. */
  VIDEO_FORM_VALIDATION_ERROR: 'video_form_validation_error',
  VIDEO_FORM_SUBMITTED: 'video_form_submitted',
  VIDEO_SAMPLE_PLAYED: 'video_sample_played',
  /** Clic en un cross-sell de los vídeos fuera de la landing: `placement` (home | guide | delivery | public-chat) y `target` (letter | book | call | shop). */
  VIDEO_CROSS_SELL_CLICKED: 'video_cross_sell_clicked',
  /**
   * Se abrió la página de entrega (`/tu-video/<token>`), del padre o de alguien
   * con quien la compartió. `video_order_id` (id interno, nunca el token) y
   * `delivery_state`; se repite si el estado cambia con la página abierta.
   */
  VIDEO_DELIVERY_VIEWED: 'video_delivery_viewed',
  /** Clic en «Descargar» de la entrega: `video_order_id`. */
  VIDEO_DELIVERY_DOWNLOADED: 'video_delivery_downloaded',
  /** La hoja nativa de compartir terminó (no cuenta cancelar): `video_order_id`. */
  VIDEO_DELIVERY_SHARED: 'video_delivery_shared',
  /**
   * Embudo del personaje propio en el dashboard. Props: `placement` (top |
   * bottom, ver lib/custom-character-placement) y `has_character`; en
   * wizard_opened, `mode` (create | redo). card_viewed sale una vez por montaje
   * cuando al menos la mitad de la card entra en pantalla, no al montarse: con
   * `bottom` suele quedar fuera de la vista y contarla inflaría su denominador.
   * El resultado de cada intento lo emite el servidor
   * (`custom_character_attempted`). call_clicked va antes del paywall: el
   * personaje propio es vídeo y la prueba gratis no lo cubre.
   */
  CUSTOM_CHARACTER_CARD_VIEWED: 'custom_character_card_viewed',
  CUSTOM_CHARACTER_WIZARD_OPENED: 'custom_character_wizard_opened',
  CUSTOM_CHARACTER_CALL_CLICKED: 'custom_character_call_clicked',
  PACKAGES_VIEWED: 'packages_viewed',
  PACKAGE_SELECTED: 'package_selected',
  CHECKOUT_CREATED: 'checkout_created',
  CHECKOUT_REDIRECTED: 'checkout_redirected',
  CHECKOUT_FAILED: 'checkout_failed',
  PURCHASE_COMPLETED: 'purchase_completed',
  PURCHASE_SUCCESS_PAGE_VIEWED: 'purchase_success_page_viewed',
  CALL_PROVIDER_CONNECTED: 'call_provider_connected',
  CALL_CHARACTER_READY: 'call_character_ready',
  CALL_CONNECTION_FAILED: 'call_connection_failed',
  TRIAL_EXPERIENCE_STARTED: 'trial_experience_started',
  TRIAL_CONVERSATION_OUTCOME: 'trial_conversation_outcome',
  PARENT_HANDOFF_COMPLETED: 'parent_handoff_completed',
  POST_TRIAL_OFFER_VIEWED: 'post_trial_offer_viewed',
  POST_TRIAL_OFFER_CLICKED: 'post_trial_offer_clicked',
  POST_TRIAL_SINGLE_OFFER_VIEWED: 'post_trial_single_offer_viewed',
  POST_TRIAL_SINGLE_OFFER_CLICKED: 'post_trial_single_offer_clicked',
  GUIDE_CALL_PREVIEW_VIEWED: 'guide_call_preview_viewed',
  GUIDE_CALL_PREVIEW_PLAYED: 'guide_call_preview_played',
  GUIDE_CALL_PREVIEW_CTA_CLICKED: 'guide_call_preview_cta_clicked',
  CALL_PREVIEW_VIEWED: 'call_preview_viewed',
  CALL_PREVIEW_PLAYED: 'call_preview_played',
  CALL_PREVIEW_CTA_CLICKED: 'call_preview_cta_clicked',
  /** CTA de cierre del artículo, el otro camino a la landing (ver GuideCtaLink). */
  GUIDE_CTA_CLICKED: 'guide_cta_clicked',
  /** Se imprimió una plantilla de guía (con o sin dejar email). */
  GUIDE_TEMPLATE_PRINTED: 'guide_template_printed',
  /** Email capturado desde una guía (POST /api/leads OK). */
  GUIDE_LEAD_SUBMITTED: 'guide_lead_submitted',
  /** Se generó una carta personalizada con el generador de la guía. */
  GUIDE_LETTER_GENERATED: 'guide_letter_generated',
  /** La carta salió del generador: `method` = web_share | download | print. */
  GUIDE_LETTER_SAVED: 'guide_letter_saved',
  /** Se generó el certificado de diente caído con el generador de la guía. */
  GUIDE_CERTIFICATE_CREATED: 'guide_certificate_created',
  /** El certificado salió del generador: `method` = web_share | download | print. */
  GUIDE_CERTIFICATE_SAVED: 'guide_certificate_saved',
  /** Se usó la calculadora de cuánto deja el ratón. */
  GUIDE_MONEY_CALCULATED: 'guide_money_calculated',
  /** Se marcó o desmarcó un diente en el mapa de dientes de leche. */
  GUIDE_TOOTH_CHART_UPDATED: 'guide_tooth_chart_updated',
  /** Salió el cuadernillo imprimible de Halloween (una acción, nueve hojas). */
  GUIDE_BOOKLET_PRINTED: 'guide_booklet_printed',
  /** Se abrió el tracker «¿Dónde está Papá Noel?» (una vez por visita). */
  SANTA_TRACKER_VIEWED: 'santa_tracker_viewed',
  /** Se abrió la ficha de una ciudad del mapa. */
  SANTA_TRACKER_CITY_OPENED: 'santa_tracker_city_opened',
  /** Se abrió el estado del trineo. */
  SANTA_TRACKER_SLEIGH_OPENED: 'santa_tracker_sleigh_opened',
  /** Se compartió la experiencia: `method` = web_share | clipboard. */
  SANTA_TRACKER_SHARED: 'santa_tracker_shared',
  /** Se activó el ambiente sonoro (nunca suena solo). */
  SANTA_TRACKER_SOUND_ENABLED: 'santa_tracker_sound_enabled',
  /** Se abrió el mapa a pantalla completa. */
  SANTA_TRACKER_FULLSCREEN_ENABLED: 'santa_tracker_fullscreen_enabled',
  /** CTA del tracker: `cta` = video_call | characters. */
  SANTA_TRACKER_CTA_CLICKED: 'santa_tracker_cta_clicked',
  MEMORY_VIDEO_OPTED_IN: 'memory_video_opted_in',
  MEMORY_VIDEO_STARTED: 'memory_video_started',
  MEMORY_VIDEO_READY: 'memory_video_ready',
  MEMORY_VIDEO_FAILED: 'memory_video_failed',
  MEMORY_VIDEO_SHARED: 'memory_video_shared',
  MEMORY_VIDEO_DOWNLOADED: 'memory_video_downloaded',
  MEMORY_VIDEO_UNSUPPORTED: 'memory_video_unsupported',
  REFERRAL_LINK_SHARED: 'referral_link_shared',
  REFERRAL_CTA_CLICKED: 'referral_cta_clicked',
  /** Se pintó el aviso de versión en otro idioma (ver lib/locale-suggestion). */
  LOCALE_SUGGESTION_SHOWN: 'locale_suggestion_shown',
  /** Se aceptó el aviso y se navegó a la versión sugerida. */
  LOCALE_SUGGESTION_ACCEPTED: 'locale_suggestion_accepted',
  /** Se cerró el aviso sin cambiar de idioma. */
  LOCALE_SUGGESTION_DISMISSED: 'locale_suggestion_dismissed',
  /**
   * Funnel del chat público (AC2.4). Props: `locale`, y `n` (1..5) en
   * message_sent. Nunca el nombre del niño ni el texto de un mensaje.
   */
  PUBLIC_CHAT_LANDING_VIEWED: 'public_chat_landing_viewed',
  PUBLIC_CHAT_STARTED: 'public_chat_started',
  PUBLIC_CHAT_MESSAGE_SENT: 'public_chat_message_sent',
  /** Se contestó el último mensaje de la sesión y aparece la tarjeta de cierre. */
  PUBLIC_CHAT_SESSION_COMPLETED: 'public_chat_session_completed',
  /** Un mensaje recibió el cierre por hoy (tope global): excluye session_completed. */
  PUBLIC_CHAT_CLOSED_FOR_TODAY: 'public_chat_closed_for_today',
  /** CTA de la tarjeta de cierre que pasa por el alta: `placement` y `cta` (signup | call). */
  PUBLIC_CHAT_SIGNUP_CTA_CLICKED: 'public_chat_signup_cta_clicked',
  /** Salud de la app nativa: murió el render process del WebView y sobrevivimos. */
  WEBVIEW_RENDER_PROCESS_GONE: 'webview_render_process_gone',
} as const
